import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganizationStore } from "@/lib/organization";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	Check,
	ChevronRight,
	ChevronsUpDown,
	Layers,
	Plus,
	Settings,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const RECENT_KEY = "omnios-recent-entities";
const MAX_RECENT = 5;

export function entityHue(name: string): number {
	let h = 0;
	for (const c of name) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
	return Math.abs(h) % 360;
}

function getRecentIds(): string[] {
	try {
		const raw = localStorage.getItem(RECENT_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function pushRecent(id: string) {
	const ids = getRecentIds().filter((x) => x !== id);
	ids.unshift(id);
	localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
}

const initials = (name: string) =>
	name
		.split(/\s+/)
		.map((w) => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

type Panel = "main" | "org";

export function OrgEntitySwitcher() {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const [panel, setPanel] = useState<Panel>("main");
	const [creatingEntity, setCreatingEntity] = useState(false);
	const [newEntityName, setNewEntityName] = useState("");

	const {
		organizations,
		selectedOrgId,
		isLoading: orgsLoading,
		setSelectedOrg,
		fetchOrganizations,
		allEntities,
		setAllEntities,
	} = useOrganizationStore();

	const {
		workspaces,
		selectedId,
		isLoading: wsLoading,
		setSelected,
		fetchWorkspaces,
		createWorkspace,
	} = useWorkspaceStore();

	const orgsFetched = useRef(false);
	useEffect(() => {
		if (orgsFetched.current) return;
		orgsFetched.current = true;
		fetchOrganizations();
	}, [fetchOrganizations]);

	const wsFetched = useRef(false);
	useEffect(() => {
		if (wsFetched.current) return;
		wsFetched.current = true;
		fetchWorkspaces();
	}, [fetchWorkspaces]);

	useEffect(() => {
		if (!open) {
			setPanel("main");
			setCreatingEntity(false);
			setNewEntityName("");
		}
	}, [open]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (
				(e.metaKey || e.ctrlKey) &&
				e.shiftKey &&
				e.key.toLowerCase() === "w"
			) {
				e.preventDefault();
				setOpen((prev) => !prev);
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, []);

	const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
	const selectedWs = workspaces.find((w) => w.id === selectedId);

	const filteredWorkspaces = useMemo(
		() =>
			selectedOrgId
				? workspaces.filter((w) => w.organizationId === selectedOrgId)
				: workspaces,
		[workspaces, selectedOrgId],
	);

	const recentIds = getRecentIds();
	const recentEntities = recentIds
		.map((id) => filteredWorkspaces.find((w) => w.id === id))
		.filter(
			(w): w is NonNullable<typeof w> => w != null && w.id !== selectedId,
		)
		.slice(0, 3);
	const showRecent =
		recentEntities.length > 0 && filteredWorkspaces.length > 4;

	const handleSelectEntity = useCallback(
		(id: string) => {
			const changed = id !== selectedId;
			setAllEntities(false);
			setSelected(id);
			pushRecent(id);
			setOpen(false);
			if (changed) queryClient.removeQueries();
		},
		[setAllEntities, setSelected, selectedId, queryClient],
	);

	const handleSelectAllEntities = useCallback(() => {
		setAllEntities(true);
		setOpen(false);
		queryClient.removeQueries();
	}, [setAllEntities, queryClient]);

	const handleSelectOrg = useCallback(
		(id: string) => {
			const changed = id !== selectedOrgId;
			setSelectedOrg(id);
			setPanel("main");
			if (changed) queryClient.removeQueries();
		},
		[setSelectedOrg, selectedOrgId, queryClient],
	);

	const handleCreateEntity = async () => {
		if (!newEntityName.trim()) return;
		const slug = newEntityName
			.toLowerCase()
			.replace(/[^a-z0-9\u0400-\u04ff]+/g, "-")
			.replace(/^-|-$/g, "");
		try {
			await createWorkspace(newEntityName.trim(), slug, selectedOrgId ?? undefined);
			queryClient.removeQueries();
			setNewEntityName("");
			setCreatingEntity(false);
			setOpen(false);
		} catch {
			// keep dialog open on error
		}
	};

	if (orgsLoading || wsLoading) {
		return (
			<div className="flex h-full items-center gap-2.5 px-2">
				<div className="h-8 w-8 rounded-lg bg-muted animate-pulse" />
				<div className="flex-1 space-y-1.5">
					<div className="h-3 w-20 rounded bg-muted animate-pulse" />
					<div className="h-2.5 w-14 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	const hasMultipleOrgs = organizations.length > 1;
	const displayName = allEntities
		? t("all_entities")
		: (selectedWs?.name ?? t("workspace_default"));

	const triggerContent = (
		<Button
			variant="ghost"
			className={cn(
				"flex h-full w-full items-center gap-2.5 px-2 rounded-none",
				"hover:bg-accent/60 active:bg-accent/80",
			)}
		>
			<Avatar
				name={
					allEntities
						? (selectedOrg?.name ?? "?")
						: (selectedWs?.name ?? "?")
				}
				avatarUrl={allEntities ? undefined : selectedWs?.avatarUrl}
				size="md"
			/>
			<div className="flex-1 min-w-0 text-left">
				<span className="block truncate text-[13px] font-semibold leading-tight">
					{displayName}
				</span>
				{selectedOrg && (
					<span className="block truncate text-[11px] text-muted-foreground/70 leading-tight mt-0.5">
						{selectedOrg.name}
					</span>
				)}
			</div>
			<ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
		</Button>
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
				</TooltipTrigger>
				{!open && (
					<TooltipContent side="right" sideOffset={8}>
						<span>{t("shortcuts_workspace")}</span>
						<kbd className="ml-2 inline-flex h-5 items-center rounded border border-border/60 bg-muted px-1 font-mono text-[10px] text-muted-foreground">
							&#8984;&#8679;W
						</kbd>
					</TooltipContent>
				)}
			</Tooltip>
			<PopoverContent
				align="start"
				side="bottom"
				sideOffset={4}
				className="w-[272px] rounded-xl border border-border/50 bg-popover p-0 shadow-xl overflow-hidden"
				onFocusOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => {
					// Don't close when focus moves to the inline create input
					if (creatingEntity) e.preventDefault();
				}}
			>
				{panel === "org" ? (
					<OrgPanel
						organizations={organizations}
						selectedOrgId={selectedOrgId}
						onSelect={handleSelectOrg}
						onBack={() => setPanel("main")}
						t={t}
					/>
				) : (
					<MainPanel
						t={t}
						selectedOrg={selectedOrg}
						hasMultipleOrgs={hasMultipleOrgs}
						allEntities={allEntities}
						filteredWorkspaces={filteredWorkspaces}
						selectedId={selectedId}
						showRecent={showRecent}
						recentEntities={recentEntities}
						creatingEntity={creatingEntity}
						newEntityName={newEntityName}
						onSwitchOrg={() => setPanel("org")}
						onSelectEntity={handleSelectEntity}
						onSelectAllEntities={handleSelectAllEntities}
						onSetCreatingEntity={setCreatingEntity}
						onSetNewEntityName={setNewEntityName}
						onCreateEntity={handleCreateEntity}
						onClose={() => setOpen(false)}
						navigate={navigate}
					/>
				)}
			</PopoverContent>
		</Popover>
	);
}

/* ------------------------------------------------------------------ */
/* Avatar                                                              */
/* ------------------------------------------------------------------ */

function Avatar({
	name,
	avatarUrl,
	size = "md",
}: {
	name: string;
	avatarUrl?: string;
	size?: "sm" | "md";
}) {
	const sizeClasses =
		size === "sm"
			? "h-6 w-6 rounded-md text-[9px]"
			: "h-8 w-8 rounded-lg text-[11px]";

	if (avatarUrl) {
		return (
			<img
				src={avatarUrl}
				alt={name}
				className={cn(sizeClasses, "object-cover shrink-0")}
			/>
		);
	}

	const hue = entityHue(name);
	return (
		<div
			className={cn(
				sizeClasses,
				"flex items-center justify-center font-semibold text-white shrink-0",
			)}
			style={{
				background: `linear-gradient(135deg, hsl(${hue}, 50%, 45%), hsl(${hue}, 45%, 35%))`,
			}}
		>
			{initials(name)}
		</div>
	);
}

/* ------------------------------------------------------------------ */
/* Main panel                                                          */
/* ------------------------------------------------------------------ */

interface WsShape {
	id: string;
	name: string;
	avatarUrl: string;
	organizationId: string;
}

function MainPanel({
	t,
	selectedOrg,
	hasMultipleOrgs,
	allEntities,
	filteredWorkspaces,
	selectedId,
	showRecent,
	recentEntities,
	creatingEntity,
	newEntityName,
	onSwitchOrg,
	onSelectEntity,
	onSelectAllEntities,
	onSetCreatingEntity,
	onSetNewEntityName,
	onCreateEntity,
	onClose,
	navigate,
}: {
	t: (key: string) => string;
	selectedOrg: { id: string; name: string } | undefined;
	hasMultipleOrgs: boolean;
	allEntities: boolean;
	filteredWorkspaces: WsShape[];
	selectedId: string | null;
	showRecent: boolean;
	recentEntities: WsShape[];
	creatingEntity: boolean;
	newEntityName: string;
	onSwitchOrg: () => void;
	onSelectEntity: (id: string) => void;
	onSelectAllEntities: () => void;
	onSetCreatingEntity: (v: boolean) => void;
	onSetNewEntityName: (v: string) => void;
	onCreateEntity: () => void;
	onClose: () => void;
	navigate: (opts: { to: string }) => void;
}) {
	const hasSearch = filteredWorkspaces.length > 4;

	return (
		<Command className="bg-popover">
			{/* Org context — compact row pinned above scroll */}
			{selectedOrg && (
				<Button
					variant="ghost"
					className={cn(
						"flex items-center gap-2 px-3 py-2 border-b border-border/40 text-[12px] w-full justify-start h-auto rounded-none",
						hasMultipleOrgs
							? "cursor-pointer"
							: "cursor-default",
					)}
					onClick={hasMultipleOrgs ? onSwitchOrg : undefined}
				>
					<Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
					<span className="flex-1 truncate font-medium text-muted-foreground">
						{selectedOrg.name}
					</span>
					<span className="text-[11px] text-muted-foreground/40 tabular-nums">
						{filteredWorkspaces.length}
					</span>
					{hasMultipleOrgs && (
						<ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/40" />
					)}
				</Button>
			)}

			{hasSearch && (
				<CommandInput placeholder={`${t("search")}…`} className="h-9" />
			)}

			<CommandList className="max-h-[340px] overflow-y-auto">
				<CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
					{t("no_results")}
				</CommandEmpty>

				{/* Recent entities */}
				{showRecent && (
					<div className="px-1 pt-1">
						<SectionLabel>{t("recent")}</SectionLabel>
						{recentEntities.map((ws) => (
							<EntityItem
								key={`recent-${ws.id}`}
								ws={ws}
								value={`recent-${ws.name}`}
								isActive={false}
								onSelect={() => onSelectEntity(ws.id)}
							/>
						))}
						<Divider />
					</div>
				)}

				{/* Entities */}
				<div className="px-1 pt-1 pb-0.5">
					<SectionLabel>{t("entities")}</SectionLabel>

					{/* Consolidated view */}
					<CommandItem
						value={t("all_entities")}
						onSelect={onSelectAllEntities}
						className={cn(
							"relative rounded-lg px-2 py-1.5 gap-2.5",
							"data-[selected=true]:bg-accent/60",
						)}
					>
						{allEntities && <ActiveBar />}
						<div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 shrink-0">
							<Layers className="h-3.5 w-3.5 text-primary/70" />
						</div>
						<div className="flex-1 min-w-0">
							<span className="block truncate text-sm font-medium">
								{t("all_entities")}
							</span>
							<span className="block truncate text-[11px] leading-tight text-muted-foreground/60">
								{t("all_entities_hint")}
							</span>
						</div>
						{allEntities && (
							<Check className="h-3.5 w-3.5 shrink-0 text-primary" />
						)}
					</CommandItem>

					{filteredWorkspaces.map((ws) => {
						const isActive = ws.id === selectedId && !allEntities;
						return (
							<EntityItem
								key={ws.id}
								ws={ws}
								value={`entity-${ws.id}-${ws.name}`}
								isActive={isActive}
								onSelect={() => onSelectEntity(ws.id)}
							/>
						);
					})}

					{filteredWorkspaces.length === 0 && (
						<div className="py-6 text-center">
							<Building2 className="mx-auto h-5 w-5 text-muted-foreground/30" />
							<p className="mt-1.5 text-[12px] text-muted-foreground/50">
								{t("no_results")}
							</p>
						</div>
					)}
				</div>

				{/* Actions */}
				<Divider />
				<div className="px-1 pb-1">
					{creatingEntity ? (
						<div className="flex items-center gap-1.5 px-2 py-1.5">
							<Input
								placeholder={t("entity_name_placeholder")}
								value={newEntityName}
								onChange={(e) =>
									onSetNewEntityName(e.target.value)
								}
								onKeyDown={(e) => {
									if (e.key === "Enter") onCreateEntity();
									if (e.key === "Escape")
										onSetCreatingEntity(false);
								}}
								className="h-7 text-sm"
								autoFocus
							/>
							<Button
								size="sm"
								className="h-7 px-2.5 text-xs shrink-0"
								onClick={onCreateEntity}
								disabled={!newEntityName.trim()}
							>
								{t("add")}
							</Button>
						</div>
					) : (
						<CommandItem
							value="create-entity"
							onSelect={() => onSetCreatingEntity(true)}
							className="rounded-lg px-2 py-1.5 gap-2.5 text-muted-foreground data-[selected=true]:bg-accent/60 data-[selected=true]:text-accent-foreground"
						>
							<Plus className="h-4 w-4" />
							<span className="text-[13px]">
								{t("new_entity")}
							</span>
						</CommandItem>
					)}
					<CommandItem
						value="entity-settings"
						onSelect={() => {
							onClose();
							navigate({ to: "/settings" });
						}}
						className="rounded-lg px-2 py-1.5 gap-2.5 text-muted-foreground data-[selected=true]:bg-accent/60 data-[selected=true]:text-accent-foreground"
					>
						<Settings className="h-4 w-4" />
						<span className="text-[13px]">{t("settings")}</span>
					</CommandItem>
					<CommandItem
						value="members"
						onSelect={() => {
							onClose();
							navigate({ to: "/settings/members" });
						}}
						className="rounded-lg px-2 py-1.5 gap-2.5 text-muted-foreground data-[selected=true]:bg-accent/60 data-[selected=true]:text-accent-foreground"
					>
						<Users className="h-4 w-4" />
						<span className="text-[13px]">{t("members")}</span>
					</CommandItem>
				</div>
			</CommandList>
		</Command>
	);
}

/* ------------------------------------------------------------------ */
/* Entity item                                                         */
/* ------------------------------------------------------------------ */

function EntityItem({
	ws,
	value,
	isActive,
	onSelect,
}: {
	ws: WsShape;
	value: string;
	isActive: boolean;
	onSelect: () => void;
}) {
	return (
		<CommandItem
			value={value}
			onSelect={onSelect}
			className={cn(
				"relative rounded-lg px-2 py-1.5 gap-2.5",
				"data-[selected=true]:bg-accent/60",
			)}
		>
			{isActive && <ActiveBar />}
			<Avatar name={ws.name} avatarUrl={ws.avatarUrl} size="sm" />
			<span className="flex-1 truncate text-sm">{ws.name}</span>
			{isActive && (
				<Check className="h-3.5 w-3.5 shrink-0 text-primary" />
			)}
		</CommandItem>
	);
}

/* ------------------------------------------------------------------ */
/* Organization sub-panel                                              */
/* ------------------------------------------------------------------ */

function OrgPanel({
	organizations,
	selectedOrgId,
	onSelect,
	onBack,
	t,
}: {
	organizations: { id: string; name: string }[];
	selectedOrgId: string | null;
	onSelect: (id: string) => void;
	onBack: () => void;
	t: (key: string) => string;
}) {
	return (
		<div>
			<Button
				variant="ghost"
				className="flex w-full items-center gap-2 px-3 py-2.5 text-[13px] font-medium justify-start h-auto rounded-none border-b border-border/40"
				onClick={onBack}
			>
				<ArrowLeft className="h-3.5 w-3.5 text-muted-foreground" />
				{t("organization")}
			</Button>
			<div className="p-1.5">
				{organizations.map((org) => {
					const isSelected = org.id === selectedOrgId;
					return (
						<Button
							key={org.id}
							variant="ghost"
							className={cn(
								"relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 justify-start h-auto",
								isSelected && "bg-accent/40",
							)}
							onClick={() => onSelect(org.id)}
						>
							{isSelected && <ActiveBar />}
							<Avatar name={org.name} size="sm" />
							<span className="flex-1 truncate text-sm font-medium">
								{org.name}
							</span>
							{isSelected && (
								<Check className="h-3.5 w-3.5 shrink-0 text-primary" />
							)}
						</Button>
					);
				})}
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ */
/* Shared primitives                                                   */
/* ------------------------------------------------------------------ */

/** Colored left-edge bar indicating the currently active item */
function ActiveBar() {
	return (
		<div className="absolute left-0.5 top-1.5 bottom-1.5 w-[3px] rounded-full bg-primary" />
	);
}

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<div className="px-2 pt-1 pb-0.5">
			<span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/40">
				{children}
			</span>
		</div>
	);
}

function Divider() {
	return <div className="mx-2 my-1 border-t border-border/30" />;
}
